import { createContext, useEffect, useState } from "react";

// ---------------------- Interfaces / Types

export type Theme = "light" | "dark" | "system";

interface IThemeContextType {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

// ---------------------- Context

export const ThemeContext = createContext<IThemeContextType | undefined>(undefined);

// ---------------------- Props

type ThemeProviderProps = {
  children: React.ReactNode;
};

// ---------------------- Provider

export function ThemeProvider({ children }: ThemeProviderProps) {
  // --------- Variables / Hooks

  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem("theme") as Theme | null;
    return savedTheme === "light" || savedTheme === "dark" || savedTheme === "system"
      ? savedTheme
      : "system";
  });

  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
  );

  // --------- Effects

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const updateTheme = () => {
      const newResolvedTheme = theme === "system" ? (mediaQuery.matches ? "dark" : "light") : theme;

      setResolvedTheme(newResolvedTheme);
      root.classList.remove("light", "dark");
      root.classList.add(newResolvedTheme);
    };

    updateTheme();
    localStorage.setItem("theme", theme);

    if (theme === "system") {
      mediaQuery.addEventListener("change", updateTheme);
      return () => mediaQuery.removeEventListener("change", updateTheme);
    }
  }, [theme]);

  // --------- JSX

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
